import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  to: string;
}

const navItems: NavItem[] = [
  { label: '首页', to: '/' },
  { label: '最新', to: '/category/latest' },
  { label: '热门', to: '/category/hot' },
  { label: '经典', to: '/category/classic' },
  { label: '关于', to: '/about' },
  { label: '常见问题', to: '/faq' },
];

const keywordSuggestions = ['夜色霓虹', '午夜列车', '京都雨停之后', '都市', '海边'];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [searchOpen]);

  useEffect(() => {
    const lock = searchOpen || menuOpen;
    document.body.style.overflow = lock ? 'hidden' : '';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [searchOpen, menuOpen]);

  const submitSearch = (value: string) => {
    const next = value.trim();
    if (!next) return;
    navigate(`/search?q=${encodeURIComponent(next)}`);
    setKeyword('');
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const solid = scrolled || !isHome;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          solid
            ? 'bg-background-50/95 backdrop-blur-xl border-b border-background-200/70'
            : 'bg-gradient-to-b from-background-50/80 via-background-50/20 to-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4 md:gap-8">
          <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer" aria-label="av免费网站 首页">
            <span className="w-10 h-10 md:w-11 md:h-11 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 flex items-center justify-center font-label text-lg md:text-xl tracking-wider">
              AV
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-base md:text-lg font-bold tracking-tight text-foreground-950">
                av免费网站
              </span>
              <span className="hidden sm:block mt-1.5 text-[10px] md:text-[11px] tracking-[0.24em] text-foreground-500 font-label uppercase">
                Premium Free Cinema
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'text-primary-400'
                      : 'text-foreground-700 hover:text-foreground-950'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute left-1/2 -bottom-0.5 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <span className="hidden md:inline-flex items-center h-7 px-2.5 rounded-md border border-accent-400/40 text-[11px] tracking-widest text-accent-300 font-label">
              18+
            </span>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="inline-flex items-center gap-2 h-10 px-3 md:px-4 rounded-md bg-background-200/70 border border-background-300/60 text-foreground-700 hover:text-foreground-950 hover:bg-background-300/70 transition-colors cursor-pointer whitespace-nowrap"
              aria-label="打开搜索"
            >
              <i className="ri-search-line text-base w-5 h-5 flex items-center justify-center" />
              <span className="hidden sm:inline text-sm">搜索影片</span>
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden w-10 h-10 rounded-md bg-background-200/70 border border-background-300/60 text-foreground-800 flex items-center justify-center cursor-pointer"
              aria-label="打开菜单"
            >
              <i className="ri-menu-3-line text-xl" />
            </button>
          </div>
        </div>
      </header>

      {searchOpen && (
        <div className="fixed inset-0 z-[70] bg-background-50/95 backdrop-blur-xl animate-fade-in">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pt-24 md:pt-32">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs tracking-[0.3em] text-foreground-500 font-label uppercase">Search the library</p>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="w-10 h-10 rounded-md border border-background-300/70 text-foreground-700 hover:text-foreground-950 flex items-center justify-center cursor-pointer"
                aria-label="关闭搜索"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch(keyword);
              }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
                <input
                  ref={inputRef}
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入影片标题、演员或标签"
                  className="w-full h-14 pl-12 pr-4 rounded-md bg-background-100 border border-background-300/70 text-base text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 focus:ring-2 focus:ring-primary-500/20 transition"
                />
              </div>
              <button
                type="submit"
                className="h-14 px-7 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始搜索
              </button>
            </form>
            <div className="mt-8">
              <p className="text-xs text-foreground-500 mb-3">热门搜索</p>
              <div className="flex flex-wrap gap-2">
                {keywordSuggestions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => submitSearch(item)}
                    className="h-9 px-4 rounded-full bg-background-200/70 border border-background-300/60 text-sm text-foreground-700 hover:text-foreground-950 hover:border-primary-500/50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-10 text-xs leading-relaxed text-foreground-500">
              站内所有影片资料均为虚构示例，搜索仅做前端匹配演示，不连接任何真实片源。
            </p>
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="fixed inset-0 z-[65] bg-background-50 lg:hidden animate-fade-in overflow-y-auto">
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/70">
            <span className="text-base font-bold">av免费网站</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 rounded-md border border-background-300/70 text-foreground-700 flex items-center justify-center cursor-pointer"
              aria-label="关闭菜单"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="px-4 py-6">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch(keyword);
              }}
              className="flex gap-2"
            >
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 w-5 h-5 flex items-center justify-center" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索影片"
                  className="w-full h-12 pl-11 pr-3 rounded-md bg-background-100 border border-background-300/70 text-sm outline-none focus:border-primary-500/70 placeholder:text-foreground-500"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <nav className="mt-8 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-4 border-b border-background-200/60 text-lg cursor-pointer ${
                      isActive ? 'text-primary-400' : 'text-foreground-900'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <i className="ri-arrow-right-up-line text-foreground-500 w-5 h-5 flex items-center justify-center" />
                </NavLink>
              ))}
            </nav>

            <div className="mt-8 flex flex-wrap gap-2">
              {['最新', '热门', '经典', '搜索'].map((tag) => (
                <span
                  key={tag}
                  className="h-8 px-3 rounded-full bg-secondary-100 text-secondary-900 text-xs flex items-center"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="mt-8 text-xs leading-relaxed text-foreground-500">
              本站为影视门户演示站，内容均为虚构示例，仅面向 18 岁以上用户。播放区为前端交互演示，不提供真实视频。
            </p>
          </div>
        </div>
      )}
    </>
  );
}